import {
  StationGraph,
  Connection,
  Station,
  Line,
  RouteResult,
  RouteStep,
  RouteOptions,
  RouteComparison,
  RoutePreference,
  FareData,
} from '../types/metro.types';
import { calculateFare } from './fareCalculator';

interface PathNode {
  stationId: string;
  line: string;
  distanceKm: number;
  timeMinutes: number;
}

/**
 * Build station graph from connections
 */
export const buildGraph = (connections: Connection[]): StationGraph => {
  const graph: StationGraph = new Map();

  for (const conn of connections) {
    // Add forward connection
    if (!graph.has(conn.from)) {
      graph.set(conn.from, []);
    }
    graph.get(conn.from)!.push(conn);

    // Add reverse connection
    if (!graph.has(conn.to)) {
      graph.set(conn.to, []);
    }
    graph.get(conn.to)!.push({
      from: conn.to,
      to: conn.from,
      line: conn.line,
      distanceKm: conn.distanceKm,
      timeMin: conn.timeMin,
    });
  }

  return graph;
};

/**
 * Check if a station should be avoided
 */
const shouldAvoidStation = (
  station: Station | undefined,
  options?: RouteOptions,
): boolean => {
  if (!station) return false;
  if (station.status === 'closed') return true;
  if (options?.avoidCrowded && station.crowdLevel === 'very_high') return true;
  if (options?.hasLuggage && station.crowdLevel === 'very_high') return true;
  return false;
};

/**
 * Find shortest route using BFS
 */
export const findShortestPath = (
  graph: StationGraph,
  source: string,
  destination: string,
  stations?: Station[],
  options?: RouteOptions,
): string[] | null => {
  if (source === destination) return null;

  const getStation = (id: string) => stations?.find(s => s.id === id);
  const queue: string[][] = [[source]];
  const visited = new Set<string>([source]);

  while (queue.length > 0) {
    const path = queue.shift()!;
    const current = path[path.length - 1];

    if (current === destination) {
      return path;
    }

    const connections = graph.get(current) || [];
    for (const conn of connections) {
      if (!visited.has(conn.to)) {
        const targetStation = getStation(conn.to);
        if (conn.to !== destination && shouldAvoidStation(targetStation, options)) {
          continue;
        }
        visited.add(conn.to);
        queue.push([...path, conn.to]);
      }
    }
  }

  return null;
};

/**
 * Find path with minimum interchanges
 */
export const findMinInterchangePath = (
  graph: StationGraph,
  source: string,
  destination: string,
  stations?: Station[],
  options?: RouteOptions,
): string[] | null => {
  if (source === destination) return null;

  const getStation = (id: string) => stations?.find(s => s.id === id);
  const best = new Map<string, { interchanges: number; prev: string | null; prevLine: string | null }>();
  const buckets: Array<Array<{ stationId: string; line: string | null }>> = [];

  for (let i = 0; i < 20; i++) buckets.push([]);

  best.set(`${source}:null`, { interchanges: 0, prev: null, prevLine: null });
  buckets[0].push({ stationId: source, line: null });

  let currentBucket = 0;
  let foundDestination: { line: string | null; interchanges: number } | null = null;

  while (currentBucket < buckets.length) {
    while (currentBucket < buckets.length && buckets[currentBucket].length === 0) {
      currentBucket++;
    }
    if (currentBucket >= buckets.length) break;

    if (foundDestination && currentBucket > foundDestination.interchanges) {
      break;
    }

    const current = buckets[currentBucket].pop()!;
    const stateKey = `${current.stationId}:${current.line}`;
    const currentBest = best.get(stateKey);

    if (!currentBest || currentBest.interchanges < currentBucket) {
      continue;
    }

    if (current.stationId === destination) {
      if (!foundDestination || currentBucket < foundDestination.interchanges) {
        foundDestination = { line: current.line, interchanges: currentBucket };
      }
      continue;
    }

    const connections = graph.get(current.stationId) || [];

    for (const conn of connections) {
      const targetStation = getStation(conn.to);
      if (conn.to !== destination && shouldAvoidStation(targetStation, options)) {
        continue;
      }

      const isInterchange = current.line !== null && conn.line !== current.line;
      const newInterchanges = currentBucket + (isInterchange ? 1 : 0);

      if (foundDestination && newInterchanges > foundDestination.interchanges) {
        continue;
      }

      const nextStateKey = `${conn.to}:${conn.line}`;
      const existingBest = best.get(nextStateKey);

      if (!existingBest || newInterchanges < existingBest.interchanges) {
        best.set(nextStateKey, {
          interchanges: newInterchanges,
          prev: current.stationId,
          prevLine: current.line,
        });

        if (newInterchanges < buckets.length) {
          buckets[newInterchanges].push({ stationId: conn.to, line: conn.line });
        }
      }
    }
  }

  if (!foundDestination) return null;

  let bestDestState: string | null = null;
  let minInterchanges = Infinity;

  for (const [key, value] of best.entries()) {
    if (key.startsWith(`${destination}:`) && value.interchanges < minInterchanges) {
      bestDestState = key;
      minInterchanges = value.interchanges;
    }
  }

  if (!bestDestState) return null;

  const path: string[] = [];
  let currentKey: string | null = bestDestState;

  while (currentKey) {
    const [stationId] = currentKey.split(':');
    path.unshift(stationId);
    const state = best.get(currentKey);
    if (!state || !state.prev) break;
    currentKey = `${state.prev}:${state.prevLine}`;
  }

  return path.length > 1 ? path : null;
};

/**
 * Get detailed path information
 */
export const getDetailedPath = (
  graph: StationGraph,
  path: string[],
): PathNode[] => {
  const detailedPath: PathNode[] = [];

  for (let i = 0; i < path.length; i++) {
    if (i === 0) {
      detailedPath.push({
        stationId: path[i],
        line: '',
        distanceKm: 0,
        timeMinutes: 0,
      });
      continue;
    }

    const prevStation = path[i - 1];
    const currentStation = path[i];
    const connections = graph.get(prevStation) || [];
    const connection = connections.find(c => c.to === currentStation);

    if (connection) {
      detailedPath.push({
        stationId: currentStation,
        line: connection.line,
        distanceKm: connection.distanceKm,
        timeMinutes: connection.timeMin,
      });
    }
  }

  return detailedPath;
};

/**
 * Detect interchange points
 */
export const detectInterchanges = (detailedPath: PathNode[]): number[] => {
  const interchangeIndices: number[] = [];

  for (let i = 1; i < detailedPath.length - 1; i++) {
    const currentLine = detailedPath[i].line;
    const nextLine = detailedPath[i + 1].line;

    if (currentLine && nextLine && currentLine !== nextLine) {
      interchangeIndices.push(i);
    }
  }

  return interchangeIndices;
};

/**
 * Calculate total distance
 */
export const calculateTotalDistance = (detailedPath: PathNode[]): number => {
  return detailedPath.reduce((total, node) => total + node.distanceKm, 0);
};

/**
 * Calculate total time
 */
export const calculateTotalTime = (
  detailedPath: PathNode[],
  interchangeCount: number,
  interchangeTimeMinutes: number = 5,
): number => {
  const travelTime = detailedPath.reduce(
    (total, node) => total + node.timeMinutes,
    0,
  );
  return travelTime + interchangeCount * interchangeTimeMinutes;
};

/**
 * Get train direction - handles both main line and branch lines
 * Based on official Delhi Metro platform patterns:
 * - Platform 1: towards terminal2 / branch end (e.g., Vaishali, Noida)
 * - Platform 2: towards terminal1 (e.g., Dwarka)
 */
const getTrainDirection = (
  line: Line,
  fromStationId: string,
  toStationId: string,
): { terminal: string; platformHint: number } => {
  const mainStations = line.stations;
  const branchStations = line.branchStations || [];

  const fromMainIndex = mainStations.indexOf(fromStationId);
  const toMainIndex = mainStations.indexOf(toStationId);
  const fromBranchIndex = branchStations.indexOf(fromStationId);
  const toBranchIndex = branchStations.indexOf(toStationId);

  const isFromOnBranch = fromBranchIndex !== -1 && fromMainIndex === -1;
  const isToOnBranch = toBranchIndex !== -1 && toMainIndex === -1;
  const isFromOnMain = fromMainIndex !== -1;
  const isToOnMain = toMainIndex !== -1;

  // Case 1: Both on main line
  if (isFromOnMain && isToOnMain) {
    const isTowardsTerminal2 = toMainIndex > fromMainIndex;
    return {
      terminal: isTowardsTerminal2 ? line.terminal2 : line.terminal1,
      platformHint: isTowardsTerminal2 ? 1 : 2,
    };
  }

  // Case 2: From branch towards main line (towards terminal1)
  // e.g., Laxmi Nagar → Mandi House (going towards Dwarka direction)
  if (isFromOnBranch && (isToOnMain || (isToOnBranch && toBranchIndex < fromBranchIndex))) {
    return {
      terminal: line.terminal1,
      platformHint: 2, // Platform 2 towards Dwarka
    };
  }

  // Case 3: From main/junction towards branch end
  // e.g., Yamuna Bank → Vaishali
  if ((isFromOnMain || fromBranchIndex === 0) && isToOnBranch && toBranchIndex > 0) {
    // Use branch terminal name if available, otherwise use terminal2
    const branchTerminal = branchStations[branchStations.length - 1];
    return {
      terminal: line.branchTerminal || branchTerminal || line.terminal2,
      platformHint: 1, // Platform 1 towards Vaishali
    };
  }

  // Case 4: Both on branch, going towards branch end (away from junction)
  if (isFromOnBranch && isToOnBranch && toBranchIndex > fromBranchIndex) {
    const branchTerminal = branchStations[branchStations.length - 1];
    return {
      terminal: line.branchTerminal || branchTerminal || line.terminal2,
      platformHint: 1,
    };
  }

  // Fallback: default to terminal1 direction
  return { terminal: line.terminal1, platformHint: 2 };
};

/**
 * Build route steps
 */
const buildRouteSteps = (
  path: string[],
  detailedPath: PathNode[],
  interchangeIndices: number[],
  stations: Station[],
  lines: Line[],
): RouteStep[] => {
  const getStation = (id: string) => stations.find(s => s.id === id);
  const getLine = (id: string) => lines.find(l => l.id === id);

  // Create a default line for fallback
  const defaultLine: Line = {
    id: 'unknown',
    name: 'Metro Line',
    color: 'blue',
    colorHex: '#4A90D9',
    stations: [],
    terminal1: '',
    terminal2: '',
  };

  const steps: RouteStep[] = [];
  let currentLine: Line = defaultLine;
  let cumulativeTime = 0;
  let currentTowards: string | undefined;

  // Safety check for empty path
  if (detailedPath.length === 0) {
    return steps;
  }

  for (let i = 0; i < detailedPath.length; i++) {
    const node = detailedPath[i];
    const station = getStation(node.stationId);
    const line = node.line ? getLine(node.line) : undefined;

    if (!station) continue;

    if (i > 0) {
      cumulativeTime += node.timeMinutes;
    }

    if (i === 0) {
      // Get the boarding line from the next node if available
      const nextNode = detailedPath.length > 1 ? detailedPath[1] : undefined;
      const boardingLine = nextNode?.line ? getLine(nextNode.line) : (line || currentLine);

      let platformNumber: string | undefined;
      if (boardingLine && path.length > 1) {
        const { terminal, platformHint } = getTrainDirection(boardingLine, path[0], path[1]);
        currentTowards = terminal;
        currentLine = boardingLine;
        platformNumber = `${platformHint}`;
      }

      steps.push({
        station,
        line: boardingLine || currentLine,
        action: 'board',
        towards: currentTowards,
        direction: currentTowards ? `Towards ${currentTowards}` : undefined,
        platform: platformNumber,
        cumulativeTimeMinutes: 0,
        segmentTimeMinutes: 0,
      });
    } else if (i === detailedPath.length - 1) {
      steps.push({
        station,
        line: line || currentLine,
        action: 'exit',
        towards: currentTowards,
        cumulativeTimeMinutes: cumulativeTime,
        segmentTimeMinutes: node.timeMinutes,
      });
    } else if (interchangeIndices.includes(i)) {
      // Safely get next line
      const nextNode = i + 1 < detailedPath.length ? detailedPath[i + 1] : undefined;
      const nextLine = nextNode?.line ? getLine(nextNode.line) : undefined;
      cumulativeTime += 5;

      let newTowards: string | undefined;
      let newPlatform: string | undefined;
      if (nextLine && i + 1 < path.length) {
        const { terminal, platformHint } = getTrainDirection(nextLine, path[i], path[i + 1]);
        newTowards = terminal;
        newPlatform = `${platformHint}`;
      }

      steps.push({
        station,
        line: line || currentLine,
        action: 'interchange',
        towards: newTowards,
        direction: nextLine?.name,
        platform: newPlatform,
        cumulativeTimeMinutes: cumulativeTime,
        segmentTimeMinutes: node.timeMinutes,
      });

      currentTowards = newTowards;
      if (nextLine) currentLine = nextLine;
    } else {
      steps.push({
        station,
        line: line || currentLine,
        action: 'travel',
        towards: currentTowards,
        cumulativeTimeMinutes: cumulativeTime,
        segmentTimeMinutes: node.timeMinutes,
      });
    }

    if (line) currentLine = line;
  }

  return steps;
};

/**
 * Build complete route result
 */
export const findRoute = (
  graph: StationGraph,
  source: string,
  destination: string,
  stations: Station[],
  lines: Line[],
  fareData: FareData,
  options?: RouteOptions,
): RouteResult | null => {
  const routeType = options?.preference || 'shortest';

  let path: string[] | null;
  if (routeType === 'minInterchange' || options?.hasLuggage) {
    path = findMinInterchangePath(graph, source, destination, stations, options);
  } else {
    path = findShortestPath(graph, source, destination, stations, options);
  }

  if (!path) return null;

  const getStation = (id: string) => stations.find(s => s.id === id);
  const sourceStation = getStation(source);
  const destinationStation = getStation(destination);

  if (!sourceStation || !destinationStation) return null;

  const detailedPath = getDetailedPath(graph, path);
  const interchangeIndices = detectInterchanges(detailedPath);
  const totalDistance = calculateTotalDistance(detailedPath);
  const totalTime = calculateTotalTime(detailedPath, interchangeIndices.length);
  const steps = buildRouteSteps(path, detailedPath, interchangeIndices, stations, lines);
  const fare = calculateFare(totalDistance, fareData);

  return {
    source: sourceStation,
    destination: destinationStation,
    path,
    steps,
    totalStations: path.length,
    totalInterchanges: interchangeIndices.length,
    totalDistanceKm: totalDistance,
    estimatedTimeMinutes: totalTime,
    fare,
    routeType,
  };
};

/**
 * Compare routes
 */
export const compareRoutes = (
  graph: StationGraph,
  source: string,
  destination: string,
  stations: Station[],
  lines: Line[],
  fareData: FareData,
  options?: RouteOptions,
): RouteComparison => {
  const shortestRoute = findRoute(
    graph, source, destination, stations, lines, fareData,
    { ...options, preference: 'shortest' }
  );

  const minInterchangeRoute = findRoute(
    graph, source, destination, stations, lines, fareData,
    { ...options, preference: 'minInterchange' }
  );

  const areSameRoute = !shortestRoute || !minInterchangeRoute ||
    (shortestRoute.path.length === minInterchangeRoute.path.length &&
      shortestRoute.path.every((s, i) => s === minInterchangeRoute.path[i]));

  let recommendation: RoutePreference = 'shortest';
  if (options?.hasLuggage || options?.needsAccessibility) {
    recommendation = 'minInterchange';
  } else if (shortestRoute && minInterchangeRoute) {
    const timeDiff = minInterchangeRoute.estimatedTimeMinutes - shortestRoute.estimatedTimeMinutes;
    const interchangeDiff = shortestRoute.totalInterchanges - minInterchangeRoute.totalInterchanges;
    if (interchangeDiff >= 1 && timeDiff <= 5) {
      recommendation = 'minInterchange';
    }
  }

  return {
    shortestRoute,
    minInterchangeRoute,
    areSameRoute,
    recommendation,
  };
};
